import React, { useState, useCallback, useEffect, useRef } from "react";
import "./QuantityInput.css";

const QuantityInput = ({
  value,
  onChange,
  lotSize = 35,
  freezeLimit = 1800,
  suggestions = [],
  disabled = false,
  className = "",
  compact = false,
  containerClassName = "",
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const justSelectedRef = useRef(false); // Track if value was just selected from dropdown

  // Sync localValue from parent when value prop changes
  // Skip sync only if user is actively typing (input is focused)
  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setLocalValue(value);
    }
  }, [value]);

  // Also sync when input loses focus to ensure consistency
  useEffect(() => {
    const handleFocusChange = () => {
      if (document.activeElement !== inputRef.current && localValue !== value) {
        setLocalValue(value);
      }
    };

    document.addEventListener('focusin', handleFocusChange);
    return () => document.removeEventListener('focusin', handleFocusChange);
  }, [value, localValue]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Generate all valid quantities (all multiples of lot_size up to freeze_limit)
  const generateAllQuantities = useCallback((lotSize, freezeLimit) => {
    const quantities = [];
    for (let i = lotSize; i <= freezeLimit; i += lotSize) {
      quantities.push(i);
    }
    return quantities;
  }, []);

  const validateQuantity = useCallback(
    (qty) => {
      const num = parseInt(qty);

      if (isNaN(num) || num <= 0) {
        return { valid: false, error: "Invalid quantity" };
      }

      if (num < lotSize) {
        return {
          valid: false,
          error: `Minimum ${lotSize}`,
        };
      }

      if (num % lotSize !== 0) {
        return {
          valid: false,
          error: `Must be multiple of ${lotSize}`,
        };
      }

      // Note: Do NOT validate against freezeLimit - backend handles splitting into tranches

      return { valid: true };
    },
    [lotSize],
  );

  const handleChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      setLocalValue(newValue);

      if (newValue === "") {
        setError("");
        // Show smart suggestions when empty
        setFilteredSuggestions(suggestions);
        setShowDropdown(true);
        setSelectedIndex(suggestions.length > 0 ? 0 : -1);
        return;
      }

      // Generate all valid quantities and filter based on typed number
      const allQuantities = generateAllQuantities(lotSize, freezeLimit);
      const filtered = allQuantities.filter((qty) =>
        qty.toString().includes(newValue)
      );

      // Only update if filtered list actually changed
      setFilteredSuggestions(filtered);
      setShowDropdown(filtered.length > 0);
      setSelectedIndex(filtered.length > 0 ? 0 : -1);

      // Show validation errors during typing
      const validation = validateQuantity(newValue);
      if (!validation.valid) {
        setError(validation.error);
      } else {
        onChange(parseInt(newValue, 10));
        setError("");
      }
    },
    [
      validateQuantity,
      suggestions,
      lotSize,
      freezeLimit,
      generateAllQuantities,
      onChange,
    ],
  );

  const handleBlur = useCallback(() => {
    setTouched(true);

    // Close dropdown on blur
    setShowDropdown(false);
    setSelectedIndex(-1);

    // If value was just selected from dropdown, skip validation
    // The selection handler already updated the value correctly
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }

    if (localValue === "" || localValue === "0") {
      setLocalValue(lotSize);
      onChange(lotSize);
      setError("");
      return;
    }

    const num = parseInt(localValue);
    const validation = validateQuantity(num);

    if (!validation.valid) {
      const roundedToLotSize = Math.round(num / lotSize) * lotSize;
      const corrected = Math.max(lotSize, roundedToLotSize);
      setLocalValue(corrected);
      onChange(corrected);
      setError("");
    } else {
      // BUGFIX: Update parent with valid value on blur
      // This was missing - parent never got updated when user typed valid quantity
      onChange(num);
      setError("");
    }
  }, [localValue, lotSize, onChange, validateQuantity]);

  const handleFocus = useCallback(() => {
    setTouched(true);

    // If input has value, filter based on current value
    if (localValue && localValue !== "" && localValue !== "0") {
      const allQuantities = generateAllQuantities(lotSize, freezeLimit);
      const filtered = allQuantities.filter((qty) =>
        qty.toString().includes(localValue.toString())
      );

      // Only show dropdown if we have filtered results
      if (filtered.length > 0) {
        setFilteredSuggestions(filtered);
        setShowDropdown(true);
        setSelectedIndex(0);
      }
    } else {
      // Show smart suggestions when empty
      setFilteredSuggestions(suggestions);
      setShowDropdown(true);
      setSelectedIndex(suggestions.length > 0 ? 0 : -1);
    }
  }, [suggestions, localValue, lotSize, freezeLimit, generateAllQuantities]);

  const handleKeyDown = useCallback(
    (e) => {
      if (!showDropdown || filteredSuggestions.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < filteredSuggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredSuggestions.length - 1
          );
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex >= 0) {
            handleSelectOption(filteredSuggestions[selectedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          setShowDropdown(false);
          setSelectedIndex(-1);
          inputRef.current?.blur();
          break;
        default:
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showDropdown, filteredSuggestions, selectedIndex] // handleSelectOption is stable
  );

  const handleSelectOption = useCallback(
    (qty) => {
      // Set flag to prevent blur handler from overwriting the selected value
      justSelectedRef.current = true;

      setLocalValue(qty);
      onChange(qty);
      setError("");
      setShowDropdown(false);
      setSelectedIndex(-1);

      // Remove focus to close dropdown - blur handler will skip validation
      inputRef.current?.blur();
    },
    [onChange]
  );

  const handleOptionPointerDown = useCallback(
    (e, qty) => {
      // Commit before the input blur event closes the dropdown.
      e.preventDefault();
      handleSelectOption(qty);
    },
    [handleSelectOption],
  );

  return (
    <div
      className={`quantity-input-container ${compact ? "compact" : ""} ${containerClassName}`}
      ref={containerRef}
    >
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`quantity-input ${error && touched ? "error" : ""} ${className}`}
        autoComplete="off"
      />
      {showDropdown && filteredSuggestions.length > 0 && !disabled && (
        <div className="quantity-dropdown" role="listbox">
          {filteredSuggestions.map((qty, index) => (
            <div
              key={qty}
              role="option"
              aria-selected={index === selectedIndex}
              className={`quantity-option ${index === selectedIndex ? "selected" : ""}`}
              onPointerDown={(e) => handleOptionPointerDown(e, qty)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              {qty}
            </div>
          ))}
        </div>
      )}
      {error && touched && <span className="quantity-error">{error}</span>}
    </div>
  );
};

export default QuantityInput;
